import type { SourceEntry } from "./types";
import riPublishersDoc from "./ri-publishers.json";

/**
 * Wizard / landing display catalogue.
 * Derived only from `ri-publishers.json` (same master the BFF RiPublisherMap loads).
 * Do not maintain a parallel list of sourceIds here.
 *
 * Logo CDN: https://cdn.or-cloud.io/logo/{publisherId}.png
 * Prefer CDN when a mapped GUID exists; fall back to local SVG when present.
 */

type PublisherRow = (typeof riPublishersDoc.publishers)[number];

const RI_LOGO_CDN = "https://cdn.or-cloud.io/logo/";

/** Optional display chrome only; not a catalogue of which sources exist. */
const BRAND_COLORS: Record<string, string> = {
  eba: "#003399",
  ecb: "#001d69",
  esma: "#0b3d91",
  eiopa: "#1a5276",
  ec: "#004494",
  fatf: "#1f4e79",
  wto: "#1a5f9e",
  sec: "#003366",
};

const INDUSTRY_BODY_IDS = new Set(["fatf", "wto"]);

/** Local SVGs that exist under public/img/sources/ for CDN fallback. */
const LOCAL_LOGO_IDS = new Set(["eba", "ecb", "esma", "eiopa", "ec", "fatf"]);

function riId(row: PublisherRow): string {
  return row.riPublisherId?.trim() ?? "";
}

function logoPath(row: PublisherRow): string {
  const id = riId(row);
  if (id) return `${RI_LOGO_CDN}${id}.png`;
  if (LOCAL_LOGO_IDS.has(row.sourceId)) return `/img/sources/${row.sourceId}.svg`;
  return `/img/sources/${row.sourceId}.svg`;
}

function toSourceEntry(row: PublisherRow): SourceEntry {
  return {
    sourceId: row.sourceId,
    name: row.displayName,
    shortName: row.shortName,
    country: row.country,
    kind: INDUSTRY_BODY_IDS.has(row.sourceId) ? "industry-body" : "regulator",
    logoPath: logoPath(row),
    brandColor: BRAND_COLORS[row.sourceId] ?? "#003399",
    riPublisherId: riId(row),
  };
}

/** Every row in the master list; no separate hardcoded sample catalogue. */
export const sourceCatalogue: SourceEntry[] = riPublishersDoc.publishers.map(toSourceEntry);

export function getSource(id: string): SourceEntry | undefined {
  const t = id.trim().toLowerCase();
  return sourceCatalogue.find(
    (s) => s.sourceId === t || s.riPublisherId.toLowerCase() === t,
  );
}

/** Watchlist identity: RI publisher GUID. Catalogue slugs are display-only. */
export function publisherKey(source: SourceEntry): string {
  return source.riPublisherId;
}

export function suggestedPublisherIds(sourceIds: string[]): string[] {
  return sourceIds
    .map((id) => getSource(id))
    .filter((s): s is SourceEntry => !!s && !!s.riPublisherId)
    .map(publisherKey);
}

export function searchSources(query: string): SourceEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return sourceCatalogue;
  return sourceCatalogue.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.shortName.toLowerCase().includes(q) ||
      s.country.toLowerCase().includes(q),
  );
}

/** Re-export master list for ops / debug UIs. */
export { riPublishersDoc };
